// src/admin/pros/ProFilterDrawer.jsx
import React, { useState, useEffect } from "react";

export default function ProFilterDrawer({
  open,
  filters,
  onApply,
  onClear,
  onClose,
  allCategories
}) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [categorySearch, setCategorySearch] = useState("");

  useEffect(() => {
    if (open) {
      setLocalFilters(JSON.parse(JSON.stringify(filters)));
      setCategorySearch("");
    }
  }, [open, filters]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [open, onClose]);

  if (!open) return null;

  const filteredCategories = allCategories.filter(cat =>
    cat.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleStatusChange = (statusKey) => {
    setLocalFilters(prev => ({
      ...prev,
      status: { ...prev.status, [statusKey]: !prev.status[statusKey] }
    }));
  };

  const handleCategoryToggle = (category) => {
    const newCategories = new Set(localFilters.categories);
    if (newCategories.has(category)) {
      newCategories.delete(category);
    } else {
      newCategories.add(category);
    }
    setLocalFilters(prev => ({ ...prev, categories: newCategories }));
  };

  const handleClearAll = () => {
    const defaultFilters = {
      status: { active: true, pending: true, suspended: true },
      categories: new Set(allCategories),
      ratingMin: 0,
      earningsMin: "",
      earningsMax: "",
      dateFrom: "",
      dateTo: "",
    };
    setLocalFilters(defaultFilters);
    onClear();
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.2)",
          zIndex: 299,
        }}
      />
      <div style={{
        position: "fixed",
        right: 0,
        top: 0,
        bottom: 0,
        width: 360,
        background: "#fff",
        boxShadow: "-4px 0 40px rgba(0,0,0,0.14)",
        zIndex: 300,
        display: "flex",
        flexDirection: "column",
        height: "100vh"
      }}>
        {/* Header */}
        <div style={{
          flexShrink: 0,
          height: 60,
          background: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px"
        }}>
          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 18,
            fontWeight: 700,
            color: "#0F172A"
          }}>
            Filters
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid #E2E8F0",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px"
        }}>
          {/* Status Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0F172A",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              Status
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              {["active", "pending", "suspended"].map(status => (
                <label key={status} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  color: "#334155",
                  cursor: "pointer"
                }}>
                  <input
                    type="checkbox"
                    checked={localFilters.status[status]}
                    onChange={() => handleStatusChange(status)}
                    style={{ cursor: "pointer" }}
                  />
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </label>
              ))}
            </div>
          </div>

          {/* Category Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0F172A",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              Category
            </div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fff",
              borderRadius: 8,
              padding: "8px 12px",
              border: "1px solid #E2E8F0",
              marginBottom: 12
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                <circle cx="10" cy="10" r="7" />
                <line x1="21" y1="21" x2="15" y2="15" />
              </svg>
              <input
                type="text"
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: 13,
                  width: "100%",
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              />
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {filteredCategories.map(category => (
                <label key={category} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 0",
                  fontSize: 13,
                  color: "#334155",
                  cursor: "pointer",
                  borderBottom: "1px solid #F8FAFC"
                }}>
                  <input
                    type="checkbox"
                    checked={localFilters.categories.has(category)}
                    onChange={() => handleCategoryToggle(category)}
                    style={{ cursor: "pointer" }}
                  />
                  {category}
                </label>
              ))}
            </div>
          </div>

          {/* Rating Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0F172A",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              Minimum Rating
            </div>
            <input
              type="range"
              min="0"
              max="5"
              step="0.1"
              value={localFilters.ratingMin}
              onChange={(e) => setLocalFilters(prev => ({ ...prev, ratingMin: parseFloat(e.target.value) }))}
              style={{ width: "100%", marginBottom: 8 }}
            />
            <div style={{
              fontSize: 13,
              color: "#64748B",
              textAlign: "center"
            }}>
              Show pros rated ≥ {localFilters.ratingMin.toFixed(1)} ★
            </div>
          </div>

          {/* Earnings Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0F172A",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              Earnings (KES)
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <input
                type="number"
                placeholder="Min"
                value={localFilters.earningsMin}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, earningsMin: e.target.value }))}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              />
              <input
                type="number"
                placeholder="Max"
                value={localFilters.earningsMax}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, earningsMax: e.target.value }))}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              />
            </div>
          </div>

          {/* Date Joined Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#0F172A",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              Date Joined
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <input
                type="date"
                value={localFilters.dateFrom}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              />
              <input
                type="date"
                value={localFilters.dateTo}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                style={{
                  flex: 1,
                  padding: "8px 12px",
                  border: "1px solid #E2E8F0",
                  borderRadius: 8,
                  fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          flexShrink: 0,
          borderTop: "1px solid #E2E8F0",
          padding: "16px 24px",
          background: "#fff",
          display: "flex",
          gap: 12
        }}>
          <button
            onClick={handleClearAll}
            style={{
              padding: "10px 20px",
              background: "#fff",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#64748B",
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
            Clear
          </button>
          <button
            onClick={() => onApply(localFilters)}
            style={{
              flex: 1,
              padding: "10px 20px",
              background: "#0F172A",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif"
            }}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}